import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
  Grid,
} from "@mui/material";
import {
  UploadFile as UploadFileIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import baseURL from "../../../user/config";

const MS_COLORS = {
  primary: '#2D4A3D',
  primaryLight: '#EBF5EE',
  text: '#323130',
  textSecondary: '#605D5C',
  background: '#FFFFFF',
  backgroundLight: '#F3F2F1',
  border: '#E1E1E1',
  hoverBackground: '#F0F0F0',
  white: '#FFFFFF',
  accent: '#107C10',
  red: '#D83B01',
};

const AddCampaignImagesDialog = ({ open, onClose, campaignId, onUploadSuccess }) => {
  const [newImages, setNewImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [campaignImages, setCampaignImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false); // New state for delete loading

  // Fetch existing campaign images
  const fetchCampaignImages = async () => {
    if (!campaignId) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${baseURL}/org/campaigns/${campaignId}/images`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch campaign images.');
      }
      const data = await response.json();
      setCampaignImages(data.images);
    } catch (error) {
      console.error("Error fetching images:", error);
      toast.error("Error fetching images.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchCampaignImages();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, campaignId]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      if (file.size > 2 * 1024 * 1024) { // 2MB size limit
        toast.error(`Image "${file.name}" exceeds the 2MB size limit.`);
        return false;
      }
      return true;
    });
    setNewImages(prev => [...prev, ...validFiles]);
  };

  const handleRemoveNewImage = (fileToRemove) => {
    setNewImages(prev => prev.filter(file => file !== fileToRemove));
  };

  const handleDeleteCampaignImage = async (imageId) => {
    if (!window.confirm("Are you sure you want to delete this image?")) {
      return;
    }

    setIsDeleting(true);
    try {
      const response = await fetch(`${baseURL}/org/campaigns/${campaignId}/images/${imageId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete image.');
      }

      toast.success("Image deleted successfully!");
      fetchCampaignImages(); // Re-fetch images to update the list
      onUploadSuccess(); // Notify parent of change
    } catch (error) {
      toast.error(error.message);
      console.error("Error deleting image:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUploadImages = async () => {
    if (newImages.length === 0) {
      toast.error("Please select at least one image to upload.");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    newImages.forEach((file) => {
      formData.append('images', file);
    });

    try {
      const response = await fetch(`${baseURL}/org/campaigns/${campaignId}/images`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload images.');
      }

      toast.success("Images uploaded successfully!");
      setNewImages([]);
      fetchCampaignImages();
    } catch (error) {
      toast.error(error.message);
      console.error("Error uploading images:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setNewImages([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { borderRadius: "8px", p: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" } }}>
      <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.2rem", color: MS_COLORS.text }}>
        Add and View Campaign Images
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" mb={2}>
          Upload additional images for your campaign.
        </Typography>
        <input
          accept="image/*"
          style={{ display: 'none' }}
          id="campaign-images-upload"
          type="file"
          multiple
          onChange={handleImageChange}
        />
        <label htmlFor="campaign-images-upload" style={{ display: 'block' }}>
          <Button
            variant="outlined"
            component="span"
            fullWidth
            sx={{
              backgroundColor: MS_COLORS.primary,
              textTransform: "none",
              color: 'white',
              py: 1.2,
              "&:hover": { backgroundColor: MS_COLORS.primary, opacity: 0.9 },
            }}
            startIcon={<UploadFileIcon />}
          >
            Choose Images
          </Button>
        </label>
        {newImages.length > 0 && (
          <Box sx={{ mt: 2, p: 2, border: `1px dashed ${MS_COLORS.border}`, borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Images to Upload:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {newImages.map((file, index) => (
                <Box key={index} sx={{ position: 'relative', width: 100, height: 100, borderRadius: 1, overflow: 'hidden' }}>
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Preview ${index}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveNewImage(file)} // Changed function name
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      color: MS_COLORS.white,
                      bgcolor: 'rgba(0,0,0,0.5)',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Current Campaign Images
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={2}>
            These images are already live on your campaign page.
          </Typography>
          {isLoading || isDeleting ? ( // Show loading for both fetch and delete
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : campaignImages.length === 0 ? (
            <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary }}>
              No additional images have been uploaded for this campaign.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {campaignImages.map((image, index) => (
                <Grid item xs={6} sm={4} key={image._id || index}> {/* Use image._id for key */}
                  <Box sx={{ position: 'relative', width: '100%', pt: '100%', borderRadius: 1, overflow: 'hidden' }}>
                    <img
                      src={image.image_url}
                      alt={`Campaign ${index + 1}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteCampaignImage(image.id)}
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        color: MS_COLORS.white,
                        bgcolor: MS_COLORS.red,
                        '&:hover': { bgcolor: 'darkred' }
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ pb: 1, px: 3 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: "4px", textTransform: "none" }}>
          Close
        </Button>
        <Button
          onClick={handleUploadImages}
          variant="contained"
          disabled={isUploading || newImages.length === 0}
          sx={{
            backgroundColor: MS_COLORS.primary,
            textTransform: "none",
            boxShadow: 0,
            "&:hover": { backgroundColor: MS_COLORS.primary, opacity: 0.9, boxShadow: 0 },
          }}
        >
          {isUploading ? <CircularProgress size={24} /> : "Upload Images"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCampaignImagesDialog;